"use client";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import {
  TOP_MENU_BUTTON_CLASS_NAME,
  TOP_MENU_BUTTON_OPEN_CLASS_NAME,
} from "@/components/doc/left-sidebar/top-menu/topMenuButtonStyles";
import { UnreadCountBadge } from "@/components/doc/left-sidebar/top-menu/UnreadCountBadge";
import { useSidebarUnreadCounts } from "@/components/doc/left-sidebar/top-menu/useSidebarUnreadCounts";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

import type { ReactNode } from "react";

function Tab({
  title,
  icon,
  isActive,
  unreadCount,
  unreadLabel,
  onClick,
}: {
  title: string;
  icon: ReactNode;
  isActive: boolean;
  unreadCount: number;
  unreadLabel: string;
  onClick: () => void;
}) {
  return (
    <Sidebar.Button
      title={title}
      aria-pressed={isActive}
      onClick={onClick}
      className={twMerge(TOP_MENU_BUTTON_CLASS_NAME, isActive && TOP_MENU_BUTTON_OPEN_CLASS_NAME)}
      icon={
        <span className="relative flex">
          {icon}
          <UnreadCountBadge count={unreadCount} label={unreadLabel} />
        </span>
      }
    />
  );
}

export const SidebarTabs = observer(function SidebarTabs() {
  const { view, teams, classroom } = useStore();
  const { docsUnread, channelsUnread } = useSidebarUnreadCounts();
  const t = useTranslations("documentsSidebar");

  const isStudent = classroom.isStudentIn(teams.currentTeam);

  return (
    <>
      <Tab
        title={t("documentTreePanel")}
        isActive={view.leftSidebarTab === "docs"}
        onClick={() => view.setLeftSidebarTab("docs")}
        unreadCount={docsUnread}
        unreadLabel={`${docsUnread} unread document messages`}
        icon={<Icon name="Docs" />}
      />
      {!isStudent && (
        <Tab
          title={t("chats")}
          isActive={view.leftSidebarTab === "chats"}
          onClick={() => view.setLeftSidebarTab("chats")}
          unreadCount={channelsUnread}
          unreadLabel={`${channelsUnread} unread chat messages`}
          icon={<Icon name="chat/ChatLarge" />}
        />
      )}
    </>
  );
});
